import {
  CartesiaVoicePersona,
  ElevenLabsVoicePersona,
  FlowBlockValueUsPhoneNumberSchema,
  FlowDurationUnits,
  OPENAI_REALTIME_COMPATIBLE_VOICES,
  OpenAiRealtimeVoicePersona,
  OpenAiStandardVoicePersona,
  VoiceCallBackgroundSound,
  VoiceCallFirstMessageMode,
  VoiceCallIdleMessage,
  VoiceCallLanguage,
  VoiceCallModel,
  VoiceCallOptionMode,
  VoiceCallTransferCallToolDestinationSchema,
  VoiceProvider,
  isElevenLabsCompatibleVoicePersona,
  isRealTimeExclusiveVoicePersona,
  isRealTimeModel,
  isRealtimeCompatibleVoicePersona,
} from 'flow-schema';
import { z } from 'zod';

import { isDefined } from '../../../utils.js';
import {
  ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER,
  END_CALL_MESSAGE_MAX_LENGTH,
  VOICEMAIL_MESSAGE_MAX_LENGTH,
} from '../../assistant/assistantDefinition/constants.js';
import { AssistantToolType } from '../../assistant/toolDefinition/index.js';
import { VoicePersonaProvider } from '../../assistant/voicePersona.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  DurationSchemaWithMax,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';
import { CommunicationWindowSchema } from '../base/communicationWindow.js';
import { OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES } from '../base/phoneNumberCountries.js';

const VOICE_SPEED_RANGE_BY_VOICE_PROVIDER: Partial<
  Record<VoiceProvider, { min: number; max: number; displayName: string }>
> = {
  [VoiceProvider.Cartesia]:
    ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[VoicePersonaProvider.Cartesia],
  [VoiceProvider.ElevenLabs]:
    ASSISTANT_VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[VoicePersonaProvider.ElevenLabs],
};

export const VoiceCallBlockConfigSchema = z
  .object({
    calleePhoneNumber: FlowBlockValueUsPhoneNumberSchema({
      isRequired: true,
      allowedCountries: OUTBOUND_CALL_AND_SMS_ALLOWED_COUNTRIES,
    }),
    /**
     * FIXME: callerPhoneNumberId is required in the frontend, but optional for legacy blocks
     */
    callerPhoneNumberId: z.uuid(),
    firstMessage: z.string(),
    firstMessageMode: z
      .enum(VoiceCallFirstMessageMode)
      .optional()
      .default(VoiceCallFirstMessageMode.AssistantWaitsForUser),
    prompt: z.string(),
    modelName: z.enum(VoiceCallModel).optional().default(VoiceCallModel.gpt_4_1),
    voicemailMessage: z.string().max(VOICEMAIL_MESSAGE_MAX_LENGTH).optional(),
    language: z.enum(VoiceCallLanguage).optional().default(VoiceCallLanguage.English),
    voiceProvider: z.enum(VoiceProvider).optional().default(VoiceProvider.ElevenLabs),
    voicePersona: z
      .union([
        z.enum(ElevenLabsVoicePersona),
        z.enum(OpenAiStandardVoicePersona),
        z.enum(OpenAiRealtimeVoicePersona),
        z.enum(CartesiaVoicePersona),
      ])
      .optional()
      .default(ElevenLabsVoicePersona.Sarah),
    timeout: DurationSchemaWithMax('timeout', {
      value: 4,
      units: FlowDurationUnits.Hours,
    }),
    maxCallDuration: DurationSchemaWithMax('maxCallDuration', {
      value: 1,
      units: FlowDurationUnits.Hours,
    }).optional(),
    tools: z.array(z.enum(AssistantToolType)).optional(),
    metadata: z.string().optional(),
    decisionTreeId: z.string().nullish(),
    endCallMessage: z.string().max(END_CALL_MESSAGE_MAX_LENGTH).optional(),
    emotionRecognition: z.enum(VoiceCallOptionMode).optional(),
    endCallFunction: z.enum(VoiceCallOptionMode).optional(),
    startSpeakingSmartEndpointing: z.enum(VoiceCallOptionMode).optional(),
    backgroundDenoising: z.enum(VoiceCallOptionMode).optional(),
    backgroundSound: z
      .enum(VoiceCallBackgroundSound)
      .optional()
      .default(VoiceCallBackgroundSound.Off),
    startSpeakingWaitSeconds: z.number().min(0).max(5).optional().default(2.0),
    voiceSimilarityBoost: z.number().min(0).max(1).optional().default(0.6),
    voiceSpeed: z.number().optional().default(1.0),
    voiceStability: z.number().min(0).max(1).optional().default(0.3),
    idleMessages: z.array(z.enum(VoiceCallIdleMessage)).optional(),
    idleMessageMaxSpokenCount: z.number().min(1).max(10).optional().default(3),
    idleTimeoutSeconds: z.number().min(5).max(60).optional().default(5),
    stopSpeakingNumberOfWords: z.number().min(0).max(10).optional().default(5),
    stopSpeakingVoiceSeconds: z.number().min(0).max(0.5).optional().default(0.5),
    stopSpeakingVoiceBackOffSeconds: z.number().min(0).max(10).optional().default(0.5),
    modelTemperature: z.number().min(0).max(2).optional().default(0.3),
    transferCallTool: z
      .object({
        destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
      })
      .optional(),
    toolToToolDescriptionMap: z.partialRecord(z.enum(AssistantToolType), z.string()).optional(),
    enableSsmlParsing: z.boolean().optional().default(false),
    validHours: z
      .object({
        start: z.number().min(0).max(23),
        end: z.number().min(0).max(23),
      })
      .optional(),
    preAssistantMessage: z.string().optional(),
    communicationWindow: CommunicationWindowSchema.optional(),
  })
  .refine(
    (config) => {
      // If using a realtime model, must use OpenAI voice provider
      if (isDefined(config.modelName) && isRealTimeModel(config.modelName)) {
        return config.voiceProvider === VoiceProvider.OpenAi;
      }
      return true;
    },
    {
      message:
        'OpenAI Realtime LLM Models require OpenAI as the voice provider. Please select OpenAI voice provider.',
      path: ['voiceProvider'],
    }
  )
  .refine(
    (config) => {
      // If using 11Labs voice provider, must use 11Labs voice persona
      if (config.voiceProvider === VoiceProvider.ElevenLabs && isDefined(config.voicePersona)) {
        return isElevenLabsCompatibleVoicePersona(config.voicePersona);
      }
      return true;
    },
    {
      message:
        'Voice Provider is set to 11Labs, but the selected Voice is not an 11Labs voice. Please select an 11Labs voice or change the provider.',
      path: ['voicePersona'],
    }
  )
  .refine(
    (config) => {
      // If using a realtime model, the voice must be realtime-compatible
      if (
        isDefined(config.modelName) &&
        isRealTimeModel(config.modelName) &&
        isDefined(config.voicePersona)
      ) {
        return isRealtimeCompatibleVoicePersona(config.voicePersona);
      }
      return true;
    },
    {
      message: `LLM Model is a realtime model, but the selected Voice is not realtime-compatible. Valid voices: ${OPENAI_REALTIME_COMPATIBLE_VOICES.join(', ')}.`,
      path: ['voicePersona'],
    }
  )
  .refine(
    (config) => {
      // If using realtime-exclusive voices, must use a realtime model
      if (isDefined(config.voicePersona) && isRealTimeExclusiveVoicePersona(config.voicePersona)) {
        return isDefined(config.modelName) && isRealTimeModel(config.modelName);
      }
      return true;
    },
    {
      message: `Voice is a realtime-exclusive voice (${Object.values(OpenAiRealtimeVoicePersona).join('/')}), but the LLM Model is not a realtime model. Please select a realtime model or choose a different voice.`,
      path: ['voicePersona'],
    }
  )
  .superRefine((config, ctx) => {
    if (isDefined(config.voiceProvider) && isDefined(config.voiceSpeed)) {
      const voiceSpeedRange =
        config.voiceProvider in VOICE_SPEED_RANGE_BY_VOICE_PROVIDER
          ? VOICE_SPEED_RANGE_BY_VOICE_PROVIDER[config.voiceProvider]
          : undefined;
      if (isDefined(voiceSpeedRange)) {
        if (config.voiceSpeed < voiceSpeedRange.min || config.voiceSpeed > voiceSpeedRange.max) {
          ctx.addIssue({
            path: ['voiceSpeed'],
            code: z.ZodIssueCode.custom,
            message: `Voice Provider is set to ${voiceSpeedRange.displayName}, but the voice speed is not between ${voiceSpeedRange.min} and ${voiceSpeedRange.max}. Please set the voice speed between ${voiceSpeedRange.min} and ${voiceSpeedRange.max}.`,
          });
        }
      }
    }
  });

export type VoiceCallBlockConfig = z.infer<typeof VoiceCallBlockConfigSchema>;

export const VoiceCallBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.VoiceCall),
  config: VoiceCallBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.VoiceCall),
});
export type VoiceCallBlock = z.infer<typeof VoiceCallBlockSchema>;
